import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  FileText,
  Download,
  Upload,
  Calendar,
  Building2,
  Mail,
  CheckCircle,
  AlertCircle,
  Loader2,
  ChevronRight,
  Ship,
} from "lucide-react";


export default function TenderBidPage() {
  const { tenderId, token } = useParams();
  const [tender, setTender] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTender();
  }, [tenderId, token]);

  const fetchTender = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/tender-public/${tenderId}/${token}`);
      setTender(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Tender not found or link expired");
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/tenders/${tenderId}/download-template?token=${token}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tender_bid_${tender?.name?.replace(/\s+/g, '_')?.slice(0, 30) || 'template'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded! Fill in your rates and upload.");
    } catch (err) {
      toast.error("Failed to download template");
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error("Please upload an Excel file (.xlsx or .xls)");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('token', token);

      const response = await axios.post(
        `${API_URL}/tenders/${tenderId}/upload-bid`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      toast.success(`Bid submitted successfully! ${response.data.routes_quoted} routes quoted.`);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload bid");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading tender information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Tender Not Found</h1>
          <p className="text-slate-600">{error}</p>
          <p className="text-sm text-slate-400 mt-4">
            This link may have expired or the tender has been closed.
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Bid Submitted!</h1>
          <p className="text-slate-600">
            Thank you for submitting your bid for <strong>{tender?.name}</strong>.
          </p>
          <p className="text-sm text-slate-400 mt-4">
            A confirmation email has been sent. We will notify you of the next steps.
          </p>
          <Button className="mt-6" onClick={() => setSubmitted(false)}>
            Submit Another Bid
          </Button>
        </div>
      </div>
    );
  }

  const isExpired = tender?.deadline && new Date(tender.deadline) < new Date();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-700 to-blue-900 text-white py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-2">
            <Ship className="h-8 w-8" />
            <span className="text-blue-200">Europacific Logistics</span>
          </div>
          <h1 className="text-3xl font-bold mb-2">Freight Tender</h1>
          <p className="text-blue-200">Submit your competitive pricing</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Tender Info Card */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
          <div className="bg-slate-50 px-6 py-4 border-b">
            <h2 className="text-xl font-bold text-slate-900">{tender?.name}</h2>
            <p className="text-slate-600">{tender?.description}</p>
          </div>
          
          <div className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center gap-2 text-slate-600 mb-4">
                  <Building2 className="h-5 w-5" />
                  <span className="font-medium">Owner:</span>
                  <span>{tender?.owner_name}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600 mb-4">
                  <Mail className="h-5 w-5" />
                  <span className="font-medium">Contact:</span>
                  <a href={`mailto:${tender?.owner_email}`} className="text-blue-600 hover:underline">
                    {tender?.owner_email}
                  </a>
                </div>
              </div>
              <div>
                <div className={`flex items-center gap-2 mb-4 ${isExpired ? 'text-red-600' : 'text-slate-600'}`}>
                  <Calendar className="h-5 w-5" />
                  <span className="font-medium">Deadline:</span>
                  <span className={isExpired ? 'font-bold' : ''}>
                    {new Date(tender?.deadline).toLocaleDateString('en-GB', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                    {isExpired && ' (EXPIRED)'}
                  </span>
                </div>
                {tender?.contract_period && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <FileText className="h-5 w-5" />
                    <span className="font-medium">Contract Period:</span>
                    <span>{tender.contract_period}</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="mt-4">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                tender?.stage === 'rfi' ? 'bg-blue-100 text-blue-700' :
                tender?.stage === 'round_1' ? 'bg-amber-100 text-amber-700' :
                tender?.stage === 'round_2' ? 'bg-orange-100 text-orange-700' :
                'bg-green-100 text-green-700'
              }`}>
                Stage: {tender?.stage?.replace('_', ' ').toUpperCase()}
              </span>
            </div>
          </div>
        </div>

        {/* Routes Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
          <div className="bg-slate-50 px-6 py-4 border-b">
            <h3 className="font-bold text-slate-900">Routes ({tender?.routes?.length || 0})</h3>
            <p className="text-sm text-slate-600">Please provide your rates for the following routes</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700">From</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700">To</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700">Type</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700">Frequency</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700">Volume</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {tender?.routes?.map((route, idx) => (
                  <tr key={idx} className="hover:bg-slate-50">
                    <td className="px-6 py-4">{route.from}</td>
                    <td className="px-6 py-4">{route.to}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm">
                        {route.service_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-600">{route.frequency || '-'}</td>
                    <td className="px-6 py-4 text-slate-600">{route.volume || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Requirements */}
        {tender?.requirements && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h3 className="font-bold text-slate-900 mb-2">Special Requirements</h3>
            <p className="text-slate-600 whitespace-pre-wrap">{tender.requirements}</p>
          </div>
        )}

        {/* Submission Section */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="font-bold text-slate-900 mb-4">Submit Your Bid</h3>
          
          {isExpired ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
              <p className="text-red-700 font-medium">This tender has expired</p>
              <p className="text-red-600 text-sm">Please contact {tender?.owner_email} for assistance</p>
            </div>
          ) : (
            <>
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-blue-800 mb-2">How to Submit:</h4>
                <ol className="text-sm text-blue-700 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs">1</span>
                    <span>Download the Excel template with all routes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs">2</span>
                    <span>Fill in your company details and pricing for each route</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs">3</span>
                    <span>Upload the completed file below</span>
                  </li>
                </ol>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  variant="outline"
                  className="flex-1"
                  onClick={downloadTemplate}
                >
                  <Download className="h-5 w-5 mr-2" />
                  Download XLS Template
                </Button>

                <div className="flex-1">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                    <Button
                      size="lg"
                      className="w-full bg-green-600 hover:bg-green-700"
                      disabled={uploading}
                      asChild
                    >
                      <span>
                        {uploading ? (
                          <>
                            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="h-5 w-5 mr-2" />
                            Upload Completed Bid
                          </>
                        )}
                      </span>
                    </Button>
                  </label>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-slate-500 text-sm">
          <p>Powered by Europacific Logistics d.o.o.</p>
          <p className="mt-1">Questions? Contact {tender?.owner_email}</p>
        </div>
      </div>
    </div>
  );
}
